/**
 * src/i18n.js
 *
 * i18n configuration for Orrith - Cycle of Certainty.
 * - Languages: English (en) and Kinyarwanda (rw)
 * - Translations loaded from public/locales/{lng}/{namespace}.json
 * - Fallback to English if language or key is missing
 * - Browser & localStorage detection for language preference
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { canUsePreferencesStorage } from "./utils/storageConsent";

// Initialize i18n with promise to ensure it's ready before app renders
const initPromise = i18n
  .use(HttpBackend) // Load translations from public/locales
  .use(LanguageDetector) // Detect language from browser/localStorage
  .use(initReactI18next) // Bind i18n to React
  .init({
    // Backend configuration for loading translation files
    // Supports multiple namespace files
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // Increase timeout and add retry logic for reliability
      requestOptions: {
        timeout: 10000,
      },
      // Allow loading of non-existent files gracefully (fallback to default namespace)
      addPath: "/locales/add/{{lng}}/{{ns}}",
    },

    // Fallback and supported languages
    fallbackLng: "en",
    supportedLngs: ["en", "rw"],

    // Detection order: 1. localStorage, 2. browser navigator, 3. fallback
    detection: {
      order: canUsePreferencesStorage() ? ["localStorage", "navigator"] : ["navigator"],
      caches: canUsePreferencesStorage() ? ["localStorage"] : [],
    },

    // Namespace configuration - support multiple feature-based namespace files
    // Each namespace is a separate translation file for better organization
    ns: [
      "common",           // Common UI elements, actions
      "auth",             // Authentication and account
      "profile",          // Profile view
      "product",          // Products and listings
      "settings",         // Settings page and modals
      "errors",           // Error messages
      "legal",            // Legal pages
      "farmMarketplace",  // Farm marketplace page
      "marketAnalysis",   // FarmPulse market intelligence
      "farmMark",         // FarmMark impact and gamification
      "support",          // Help center and support system
      "inbox",            // Inbox, messaging, and notifications
      "cropCatalog",      // Localized crop names and category labels
      "offers&orders"     // Offers and orders management pages
    ],
    defaultNS: "common",

    // Preload languages for better initial performance
    preload: ["en", "rw"],

    // Disable debug in production
    debug: false,

    // React options - Ensure useSuspense is false to avoid rendering before ready
    react: {
      useSuspense: false, // Disable Suspense for backward compatibility
    },

    // Load namespaces on language change
    load: "languageOnly",

    // Set wait timeout
    wait: true,
  });

// Export both i18n and the initialization promise
export { initPromise };
export default i18n;


